import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import RepairReportStatusBadge from '#/modules/crmMgmt/aftersales/repairReport/components/RepairReportStatusBadge';
import { mapRepairReportMeta } from '#/modules/crmMgmt/aftersales/repairReport/helper/formSerialize';

function displayOrDash(value) {
  if (value == null || value === '') {
    return '—';
  }
  return value;
}

function HistoryField({ label, value, preserveNewlines = false }) {
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p
        className={cn(
          'mt-0.5 text-gray-900 text-sm font-medium wrap-anywhere',
          preserveNewlines && 'whitespace-pre-line',
        )}
      >
        {value}
      </p>
    </div>
  );
}

HistoryField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  preserveNewlines: PropTypes.bool,
};

function RepairReportHero({ meta, actions, compact = false }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
        <h1
          className={cn(
            'font-bold text-gray-900',
            compact ? 'text-xl' : 'text-2xl',
          )}
        >
          {meta.code}
        </h1>
        {meta.status ? <RepairReportStatusBadge status={meta.status} /> : null}
      </div>
      {actions ? (
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {actions}
        </div>
      ) : null}
    </div>
  );
}

RepairReportHero.propTypes = {
  meta: PropTypes.shape({
    code: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
  actions: PropTypes.node,
  compact: PropTypes.bool,
};

function RepairReportInfoGrid({ meta, compact = false, hideItemSnapshot = false }) {
  return (
    <div
      className={cn(
        'mt-4 grid grid-cols-1 gap-x-4 gap-y-3 border-t border-slate-200 pt-4',
        !compact && 'sm:grid-cols-2 md:grid-cols-4',
      )}
    >
      <HistoryField label="建立" value={meta.createDate} />
      <HistoryField label="修改" value={meta.reviseDate} />
      <HistoryField label="完成" value={meta.completedAt} />
      <HistoryField label="拒修" value={meta.refusedAt} />
      {meta.cancelReason ? (
        <HistoryField label="取消" value={meta.cancelledAt} />
      ) : null}
      <HistoryField label="維修日" value={meta.repairDate} />
      <HistoryField label="檢測項數" value={meta.inspectionItemCount} />
      {hideItemSnapshot ? null : (
        <>
          <HistoryField label="產品序號" value={displayOrDash(meta.serialNo)} />
          <HistoryField label="品名" value={displayOrDash(meta.itemName)} />
          <div className={cn(!compact && 'sm:col-span-2 md:col-span-2')}>
            <HistoryField
              label="問題描述"
              value={displayOrDash(meta.problemDescription)}
            />
          </div>
          <div className={cn(!compact && 'sm:col-span-2 md:col-span-2')}>
            <HistoryField
              label="配件"
              value={displayOrDash(meta.accessories)}
              preserveNewlines
            />
          </div>
        </>
      )}
      <HistoryField label="處理方式" value={displayOrDash(meta.resolution)} />
      <div className={cn(!compact && 'sm:col-span-2 md:col-span-2')}>
        <HistoryField
          label="檢測結論"
          value={displayOrDash(meta.inspectionConclusion)}
        />
      </div>
      <div className={cn(!compact && 'sm:col-span-2 md:col-span-2')}>
        <HistoryField label="備註" value={displayOrDash(meta.remark)} />
      </div>
      {meta.refuseReason ? (
        <div className={cn(!compact && 'sm:col-span-2 md:col-span-4')}>
          <HistoryField label="拒修原因" value={meta.refuseReason} />
        </div>
      ) : null}
      {meta.cancelReason ? (
        <div className={cn(!compact && 'sm:col-span-2 md:col-span-4')}>
          <HistoryField label="取消原因" value={meta.cancelReason} />
        </div>
      ) : null}
    </div>
  );
}

RepairReportInfoGrid.propTypes = {
  meta: PropTypes.shape({
    createDate: PropTypes.string,
    reviseDate: PropTypes.string,
    completedAt: PropTypes.string,
    refusedAt: PropTypes.string,
    repairDate: PropTypes.string,
    inspectionItemCount: PropTypes.number,
    serialNo: PropTypes.string,
    itemName: PropTypes.string,
    problemDescription: PropTypes.string,
    accessories: PropTypes.string,
    resolution: PropTypes.string,
    inspectionConclusion: PropTypes.string,
    remark: PropTypes.string,
    refuseReason: PropTypes.string,
    cancelledAt: PropTypes.string,
    cancelReason: PropTypes.string,
  }).isRequired,
  compact: PropTypes.bool,
  hideItemSnapshot: PropTypes.bool,
};

const RepairReportMainInfoCard = ({
  detail,
  embedded = false,
  nested = false,
  compact = false,
  hideItemSnapshot = false,
  actions = null,
  containerClassName = '',
}) => {
  const meta = mapRepairReportMeta(detail);
  const surfacePaddingClassName = compact ? 'p-4' : 'p-5';

  const content = (
    <>
      <RepairReportHero meta={meta} actions={actions} compact={compact} />
      <RepairReportInfoGrid
        meta={meta}
        compact={compact}
        hideItemSnapshot={hideItemSnapshot}
      />
    </>
  );

  if (nested) {
    return content;
  }

  if (embedded) {
    return (
      <div className={cn('border-b border-slate-200', surfacePaddingClassName)}>
        {content}
      </div>
    );
  }

  return (
    <div className={cn('receipt-card-elevate', containerClassName)}>
      <div className={cn('receipt-card-surface', surfacePaddingClassName)}>
        {content}
      </div>
    </div>
  );
};

RepairReportMainInfoCard.propTypes = {
  detail: PropTypes.object.isRequired,
  embedded: PropTypes.bool,
  nested: PropTypes.bool,
  compact: PropTypes.bool,
  hideItemSnapshot: PropTypes.bool,
  actions: PropTypes.node,
  containerClassName: PropTypes.string,
};

export default RepairReportMainInfoCard;
